import { useParams, Link, Navigate } from 'react-router-dom';
import Seo from '@/components/Seo';
import { GUIDES, guideBySlug } from '@/lib/guides';
import { ArrowLeft, Calculator, ArrowRight, Sparkles } from 'lucide-react';

const SITE = 'https://tradesmateuk.co.uk';

function CalcCta({ calc }) {
  return (
    <div className="tm-card p-5 bg-slate-900 text-white my-8" data-testid="guide-calc-cta">
      <div className="flex items-start gap-3">
        <Calculator className="text-brand shrink-0 mt-1" size={24} />
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-brand font-semibold">Do the maths instantly</p>
          <h2 className="font-display font-bold text-xl uppercase tracking-tight mt-1">{calc.label}</h2>
          <p className="text-slate-300 text-sm mt-1">{calc.blurb}</p>
          <Link to={`/calculators/${calc.slug}`} data-testid="guide-open-calc" className="tm-btn mt-3 inline-flex">
            Open the {calc.label} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ToolCta({ cta }) {
  return (
    <div className="tm-card p-5 bg-slate-900 text-white my-8" data-testid="guide-tool-cta">
      <div className="flex items-start gap-3">
        <Calculator className="text-brand shrink-0 mt-1" size={24} />
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-brand font-semibold">Free tool</p>
          <h2 className="font-display font-bold text-xl uppercase tracking-tight mt-1">{cta.label}</h2>
          <p className="text-slate-300 text-sm mt-1">{cta.blurb}</p>
          <Link to={cta.to} data-testid="guide-open-tool" className="tm-btn mt-3 inline-flex">
            {cta.label} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function GuidePage() {
  const { slug } = useParams();
  const guide = guideBySlug(slug);
  if (!guide) return <Navigate to="/guides" replace />;

  const related = (guide.relatedGuides || []).map(guideBySlug).filter(Boolean);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.h1,
      description: guide.metaDescription,
      datePublished: '2026-06-01',
      dateModified: '2026-06-25',
      author: { '@type': 'Organization', name: 'TradesMate UK' },
      publisher: { '@type': 'Organization', name: 'TradesMate UK' },
      mainEntityOfPage: `${SITE}/guides/${guide.slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Guides', item: `${SITE}/guides` },
        { '@type': 'ListItem', position: 2, name: guide.h1, item: `${SITE}/guides/${guide.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: guide.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="guide-page">
      <Seo
        title={guide.metaTitle}
        description={guide.metaDescription}
        path={`/guides/${guide.slug}`}
        kicker={`${guide.kicker} · TradesMate`}
        jsonLd={jsonLd}
      />

      <nav className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2 flex-wrap font-semibold">
        <Link to="/guides" className="hover:text-brand">Guides</Link>
        <span>/</span>
        <span className="text-slate-700">{guide.kicker.replace('// ', '')}</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight">{guide.h1}</h1>
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2 font-semibold">Updated {guide.updated} · Estimates only — verify before quoting</p>
      </header>

      <div className="space-y-4">
        {guide.intro.map((p, i) => (
          <p key={i} className="text-slate-700 leading-relaxed">{p}</p>
        ))}
      </div>

      {guide.costTable && (
        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">{guide.costTable.caption}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200" data-testid="guide-cost-table">
              <thead>
                <tr className="bg-slate-900 text-white text-left">
                  {guide.costTable.head.map((h, i) => (
                    <th key={i} className="p-3 font-display uppercase tracking-tight text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {guide.costTable.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`p-3 border-t border-slate-200 ${ci === 0 ? 'font-medium' : 'font-mono'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {guide.calc && <CalcCta calc={guide.calc} />}
      {guide.cta && <ToolCta cta={guide.cta} />}

      <div className="space-y-8">
        {guide.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display font-semibold uppercase tracking-tight text-xl mb-3">{s.h}</h2>
            {(s.p || []).map((p, pi) => (
              <p key={pi} className="text-slate-700 leading-relaxed mb-2">{p}</p>
            ))}
            {s.ul && (
              <ul className="list-disc pl-6 space-y-1 text-slate-700 mt-2">
                {s.ul.map((li, li2) => <li key={li2}>{li}</li>)}
              </ul>
            )}
          </section>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="font-display font-bold text-xl uppercase mb-4">Frequently asked questions</h2>
        <div className="space-y-4">
          {guide.faq.map((f, i) => (
            <div key={i} className="tm-card p-4">
              <h3 className="font-bold text-base">{f.q}</h3>
              <p className="text-slate-600 text-sm mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pro signup CTA */}
      <div className="mt-10 tm-card p-6 border border-slate-200 shadow-md">
        <div className="flex items-start gap-3">
          <Sparkles className="text-brand shrink-0 mt-1" size={24} />
          <div>
            <h2 className="font-display font-bold text-lg uppercase tracking-tight">From estimate to winning quote</h2>
            <p className="text-slate-600 text-sm mt-1">
              TradesMate Pro turns these numbers into branded, professional quotes and invoices — plus live job profit tracking. Free calculators forever; start a free trial for the full kit.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link to="/register" data-testid="guide-signup-cta" className="tm-btn inline-flex">Start free</Link>
              <Link to="/pricing" className="tm-btn tm-btn-dark inline-flex">See plans</Link>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-xl uppercase mb-4">Related guides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {related.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="tm-card p-3 hover:shadow-lg transition-shadow flex items-center justify-between">
                <span className="font-medium text-sm">{g.h1}</span>
                <span className="text-brand">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Link to="/guides" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand mt-8 font-semibold">
        <ArrowLeft size={14} /> All guides
      </Link>
    </div>
  );
}
